import "core-js/stable";
import "regenerator-runtime/runtime";
import { Prop, Watch } from "vue-property-decorator";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themesAnimated from "@amcharts/amcharts4/themes/animated";
import { getModule } from "vuex-module-decorators";
import { ContractsStore } from "../../store/modules/contracts";
import Vue from "vue";

am4core.useTheme(am4themesAnimated);

/**
 *  Класс для компоненты диаграммы
 */
export class MyChartVue extends Vue {

  /** Входные Данные для диаграммы */
  @Prop() inputData!: Array<any>;

  /** Название параметра категории из АПИ */
  @Prop() categoryName!: string;

  /** Название параметра значения из АПИ */
  @Prop() valueName!: string;

  /** Название параметра кода из АПИ */
  @Prop() codeName!: string;

  /** Название параметра зцвета из АПИ */
  @Prop() colorName!: string;

  /** Значение фильтра операций */
  @Prop() filterValue!: string;

  /** Показывать график или полосу */
  @Prop() isChartHidden!: boolean;

  @Prop() onlyLine!: boolean;

  /** Данные для диаграммы */
  chartData: Array<any> = [];

  /** Общая сумма данных */
  chartSum: number = 0;

  /** Куски диаграммы */
  slices: Array<any> = [];

  /** Объект самой диаграммы */
  chart: any = null;

  /** Отслеживаем значение фильтра и запускаем ререндер чата */
  @Watch("filterValue", { immediate: true })
  changedFilter(val: any) {
    if (val)
      this.chartRender();
  }

  /** Отслеживаем входные данные и запускаем ререндер чата */
  @Watch("inputData", { immediate: true })
  changedData() {
    if (this.chart)
      this.chartRender();
  }

  /**
 * Хранилище счетов
 * @returns {ContractsStore}
 */
  get contractsStore(): ContractsStore {
    return getModule(ContractsStore, this.$store);
  }

  /**
 * Валюта активного счета
 * @returns {string} - код валюты активного счета.
 */
  get curCurrency(): string {
    return this.contractsStore.contracts.getContract(+this.$route.params.contractId)?.Data?.CurrencyCharCode;
  }

  /**
 * Выбранный кусок диаграммы
 * @returns {any} - возвращает false, когда выбранных кусков нет.
 */
  get getActiveSlice() {
    const slice = this.chartData.filter(item => item.active);
    if (slice.length > 0) return slice[0];
    return false;
  }

  /** Обрезаем нужные куски диграммы для ленточного вида. */
  getBorderRadiusSlice(i: number) {
    if (this.chartData.length !== 1) {
      if (i === 0) return "100px 0px 0px 100px";
      else if (i === this.chartData.length - 1) return "0px 100px 100px 0px";
      else return "0px";
    }
    return "100px";
  }

  /** Изменение цвета легенды при клике */
  getActiveStyles(item: any) {
    const backColor = item.active ? item.color : "";
    const textColor = item.active ? "#fff" : "";
    return `background: ${backColor}; color: ${textColor}`;
  }

  /** Выбор нужного куска диаграммы по клику на легенду */
  chooseSlice(i: number) {
    this.chartData[i].active = !this.chartData[i].active;
    this.chartData.map((item, k) => {
      if (i !== k) item.active = false;
    });
    if (this.chartData[i].active) {
      this.slices[i].setState("hover");
      this.$emit("changeActiveCategory", this.chartData[i].category);
    } else {
      this.slices[i].setState("default");
      this.$emit("changeActiveCategory", null);
    }
  }

  getRandomColor() {
      const letters = '0123456789ABCDEF';
      let color = '#';
      for (let i = 0; i < 6; i++) {
          color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
  }

  /** Рендер диаграммы */
  chartRender() {
    if (this.chart) this.chart.dispose();
    this.chartData = [];
    /** Обработка входных данных */
    this.inputData.map(item => {
      /** Проверка на наличие данной категории расходов в данных для диаграммы и фильтрация */
      if (item[this.valueName] < 0 && item.RequestState === 2) {
        if (!isNaN(item[this.valueName])) {
          const beforeObj =
            this.chartData.length > 0
              ? this.chartData.find(
                  v => v[this.codeName] == item[this.codeName]
                )
              : undefined;
          /** Если данных такой категории еще нет то дополняем массив данных и цветов */
          if (beforeObj === undefined) {
            this.chartData.push({
              category: item[this.categoryName],
              value: item[this.valueName],
              color:
                item[this.colorName] || this.getRandomColor(),
              active: false,
              [this.codeName]: item[this.codeName]
            });
            /** Если данные уже есть, то просто увеличиваем сумму расходов */
          } else {
            beforeObj.value += item[this.valueName];
          }
        }
      }
    });
    this.chartSum = this.chartData.reduce((acc, item) => (acc += item.value), 0);

    if (this.onlyLine)
      return;

    if (this.chart) return;

    /** Создание диаграммы */
    const chart = am4core.create(
      this.$refs.chartdiv as HTMLElement,
      am4charts.PieChart
    );

    /** Настройка размеров и расположения диаграммы */
    chart.innerRadius = am4core.percent(70);
    chart.width = am4core.percent(100);
    chart.height = am4core.percent(100);
    chart.valign = "top";

    /** Заполнение диаграммы данными */
    chart.data = this.chartData;

    /** Создание кусков диаграммы */
    const pieSeries = chart.series.push(new am4charts.PieSeries());
    pieSeries.dataFields.value = "value";
    pieSeries.dataFields.category = "category";

    /** Расскраска кусков диаграммы */
    pieSeries.colors.list = [];
    this.chartData.map(color => {
      pieSeries.colors.list.push(am4core.color(color.color));
    });

    /** Отключение ненужных элементов диаграммы */
    pieSeries.labels.template.disabled = true;
    pieSeries.ticks.template.disabled = true;
    if (pieSeries.tooltip) {
      pieSeries.tooltip.disabled = true;
      pieSeries.tooltip.autoTextColor = false;
    }

    const disabledState = pieSeries.slices.template.states.getKey("default");
    if (disabledState) {
      disabledState.properties.scale = 1;
      disabledState.properties.dx = 0;
      disabledState.properties.dy = 0;
      disabledState.properties.clickable = false;
    }

    // const disabledState = pieSeries.slices.template.states.create("disabled");

    /** Выделение шаблона куска диаграммы */

    const slice = pieSeries.slices.template;
    this.chartSum = chart.data.reduce((acc, item) => (acc += item.value), 0);

    /** Отключаем изменение шаблона куска диаграммы при наведении */
    /** Событие клика по шаблону куска диаграммы */
    slice.events.on("over", (e: any) => {
      if (pieSeries.tooltip) pieSeries.tooltip.disabled = false;

      const curVal =
        e.target.dataItem.value < 0
          ? e.target.dataItem.value * -1
          : e.target.dataItem.value;
      if (this.$options.filters) {
        const val = this.$options.filters.sum(
          curVal,
          false,
          this.contractsStore.contracts.getContract(+this.$route.params.contractId)?.Data?.CurrencyCharCode
        );
        const percent = (curVal / this.chartSum) * 100;
        pieSeries.slices.template.tooltipText = `{category}: ${percent.toFixed(
          2
        )}% (${val})`;
        for (let i = 0; i < pieSeries.slices.values.length; i++) {
          if (pieSeries.slices.values[i] === e.target) {
            this.chartData[i].active = !this.chartData[i].active;
          }
        }
      }
    });
    slice.events.on("out", e => {
      if (pieSeries.tooltip) pieSeries.tooltip.disabled = true;

      for (let i = 0; i < pieSeries.slices.values.length; i++) {
        if (pieSeries.slices.values[i] === e.target) {
          this.chartData[i].active = !this.chartData[i].active;
        }
      }
    });

    slice.events.disable();

    /** Вынос списка кусков диаграммы в глобальную видимость */
    chart.events.on("ready", () => {
      this.slices = pieSeries.slices.values;
      slice.setState("default");
    });

    /** Создание общей суммы расходов по центру диаграммы */
    const label = chart.seriesContainer.createChild(am4core.Label);
    if (chart.data.length > 0 && this.$options.filters)
      label.text = this.$options.filters.sum(
        this.chartSum,
        true,
        this.contractsStore.contracts.getContract(+this.$route.params.contractId)?.Data?.CurrencyCharCode
      );
    else label.text = "";
    label.horizontalCenter = "middle";
    label.verticalCenter = "middle";
    label.fontWeight = "bold";
    label.fontSize = "16px";

    /** Отключаем лого диаграммы */
    chart.logo.disabled = true;
    this.chart = chart;
  }
}
